import { addDays, format, subDays } from 'date-fns'
import pluralize from 'pluralize'
import { bool, func, number, object } from 'prop-types'
import { Component } from 'react'
import { Color } from '@opallabs/gem'
import {
  kuiperClassNames,
  TrackedDatePickerPopoverV2,
  NavigationControls,
  Popdown,
  TrackedSelect,
  Tooltip,
  UnwrappedButton,
  Icon,
} from '@opallabs/kuiper'
import { Button, Icon as LapidaryIcon } from '@opallabs/lapidary'
import { AnalyticsLayer } from '@opallabs/quanta'

import './styles.scss'

const DATE_FORMAT = 'PP'

export const COLUMN_OPTIONS = [
  {
    id: 0,
    title: 'Column',
    icon: 'view-column',
    hideText: true,
  },
  {
    id: 1,
    title: 'Seamless',
    icon: 'view-seamless',
    hideText: true,
  },
]

export const CARD_OPTIONS = [
  {
    id: 2,
    title: 'Content card',
    icon: 'card',
    hideText: true,
  },
  {
    id: 3,
    title: 'Content only',
    icon: 'cardless',
    hideText: true,
  },
]

const CARD_DISABLED_TEXT =
  'Content card is not available when viewing seamless layout'

const CARD_DISABLED_TOOLTIP = {
  content: CARD_DISABLED_TEXT,
  className: 'view-switcher-select',
  themeClass: 'white',
  popoverOptions: {
    constraints: [{ popover: 'bottom center', attachment: 'center left' }],
  },
}

export const DISABLED_CARD_OPTIONS = [
  {
    ...CARD_OPTIONS[0],
    disabled: true,
    tooltip: CARD_DISABLED_TOOLTIP,
  },
  CARD_OPTIONS[1],
]

const selectedCardOptions = (seamless) =>
  seamless ? DISABLED_CARD_OPTIONS : CARD_OPTIONS

const getTrigger = (name, action, options, currentValue) => (
  <AnalyticsLayer name={name}>
    <TrackedSelect
      arrowIcon="chevron-s"
      className="view-switcher"
      hasMenu={true}
      onSelect={action}
      options={options}
      type="dropdown"
      value={currentValue}
    />
  </AnalyticsLayer>
)

const selectedCard = (chromeless) => {
  const id = chromeless ? 3 : 2
  return CARD_OPTIONS.find((option) => option.id === id)
}

const selectedColumn = (seamless) => {
  const id = seamless ? 1 : 0
  return COLUMN_OPTIONS.find((option) => option.id === id)
}

class PlacementsHeader extends Component {
  static propTypes = {
    account: object,
    activeFilterCount: number,
    chromeless: bool,
    date: object,
    exportContentModal: func,
    filterButtonVisible: bool,
    navigateToDate: func,
    onChromeChange: func,
    onSeamlessChange: func,
    seamless: bool,
    service: object,
    startOfWeek: number,
    toggleFilterBar: func,
  }

  static defaultProps = {
    chromeless: false,
    exportContentModal: () => {},
    navigateToDate: () => {},
    seamless: false,
    service: {},
  }

  getFilterButtonLabel = () => {
    const { activeFilterCount } = this.props

    if (activeFilterCount) {
      const filter = pluralize('Filter', activeFilterCount)

      return `${activeFilterCount} Active ${filter}`
    }

    return 'Filter'
  }

  navigateToToday = () => {
    this.props.navigateToDate(new Date())
  }

  navigateBack = () => {
    const yesterday = subDays(this.props.date, 1)
    this.props.navigateToDate(yesterday)
  }

  navigateForward = () => {
    const tomorrow = addDays(this.props.date, 1)
    this.props.navigateToDate(tomorrow)
  }

  get filterBarButton() {
    if (!this.props.filterButtonVisible) return null
    const hasActiveFilters = this.props.activeFilterCount > 0

    return (
      <div className={kuiperClassNames('filter-search-wrapper')}>
        <Button
          buttonText={this.getFilterButtonLabel(this.props.activeFilterCount)}
          variant={hasActiveFilters ? 'primary' : 'ghost'}
          name="open-filter-bar"
          icon={
            <LapidaryIcon
              glyph="drawer-open"
              useHypernova={true}
              color={Color.light.Text.InvertedDefault}
            />
          }
          iconPosition="trailing"
          theme="dark"
          onClick={this.props.toggleFilterBar}
        />
      </div>
    )
  }

  get getDateText() {
    const formattedDate = format(this.props.date, DATE_FORMAT)

    return (
      <div className={kuiperClassNames('date')}>
        {formattedDate}
        <Icon glyph="chevron-lg-s" ariaMode="hidden" />
      </div>
    )
  }

  get datepicker() {
    return (
      <TrackedDatePickerPopoverV2
        className="placements-header-date-picker"
        date={this.props.date}
        dateformat={DATE_FORMAT}
        displayType="text"
        onSelect={this.props.navigateToDate}
        popoverOptions={{
          position: 'bottom',
        }}
        startOfWeek={this.props.startOfWeek}
        text={this.getDateText}
      />
    )
  }

  render() {
    return (
      <div className={kuiperClassNames('placements-header')}>
        <div className={kuiperClassNames('header-left')}>
          {this.filterBarButton}
          <Popdown heading="Previewing" content={this.datepicker} />
          <Popdown heading="Channel" content={this.props.service.name} />
          <Popdown heading="Page" content={this.props.account.name} />
        </div>
        <div className={kuiperClassNames('header-right')}>
          <div className={kuiperClassNames('view-transformation-buttons')}>
            <Tooltip
              content="Column"
              popoverHandlers={{
                interactive: false,
              }}
              theme="light"
              trigger={getTrigger(
                'seamless',
                this.props.onSeamlessChange,
                COLUMN_OPTIONS,
                selectedColumn(this.props.seamless)
              )}
            />
            <Tooltip
              content="CONTENT CARD"
              popoverHandlers={{
                interactive: false,
              }}
              theme="light"
              trigger={getTrigger(
                'chromeless',
                this.props.onChromeChange,
                selectedCardOptions(this.props.seamless),
                selectedCard(this.props.chromeless)
              )}
            />
          </div>
          <div className={kuiperClassNames('header-nav-section')}>
            <NavigationControls
              onNext={this.navigateForward}
              onPrevious={this.navigateBack}
              onToday={this.navigateToToday}
            />
          </div>
          <UnwrappedButton
            className={kuiperClassNames('csv-export')}
            onClick={this.props.exportContentModal}
            label="Export CSV"
            icon="csv-export"
            type="invisible"
          />
        </div>
      </div>
    )
  }
}

export default PlacementsHeader
